import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import * as adminApi from '../api/adminApi';
import { formatDateFull } from '../utils/formatDate';

/**
 * Event type options for the filter dropdown.
 * Mirrors the AUDIT_EVENTS enum on the backend.
 */
const EVENT_TYPES = [
  { value: '', label: 'All Events' },
  { value: 'LOGIN_SUCCESS', label: 'Login Success' },
  { value: 'LOGIN_FAILED', label: 'Login Failed' },
  { value: 'LOGOUT', label: 'Logout' },
  { value: 'PASSWORD_CHANGED', label: 'Password Changed' },
  { value: 'USER_CREATED', label: 'User Created' },
  { value: 'USER_DELETED', label: 'User Deleted' },
  { value: 'INVITE_CODE_CREATED', label: 'Invite Code Created' },
  { value: 'INVITE_CODE_USED', label: 'Invite Code Used' }
];

/**
 * Color-coded badge styles for each event category.
 * Green = success, Red = failure/destructive, Blue = info, Gray = other
 */
const eventBadgeStyles = {
  LOGIN_SUCCESS: 'bg-green-100 text-green-800',
  LOGIN_FAILED: 'bg-red-100 text-red-800',
  LOGOUT: 'bg-gray-100 text-gray-800',
  PASSWORD_CHANGED: 'bg-yellow-100 text-yellow-800',
  USER_CREATED: 'bg-blue-100 text-blue-800',
  USER_DELETED: 'bg-red-100 text-red-800',
  INVITE_CODE_CREATED: 'bg-blue-100 text-blue-800',
  INVITE_CODE_USED: 'bg-green-100 text-green-800'
};

/**
 * Human-readable labels for event types
 */
const eventLabels = {
  LOGIN_SUCCESS: 'Login Success',
  LOGIN_FAILED: 'Login Failed',
  LOGOUT: 'Logout',
  PASSWORD_CHANGED: 'Password Changed',
  USER_CREATED: 'User Created',
  USER_DELETED: 'User Deleted',
  INVITE_CODE_CREATED: 'Invite Created',
  INVITE_CODE_USED: 'Invite Used'
};

function AuditLog() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(true);

  // Filters
  const [eventFilter, setEventFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const PAGE_SIZE = 50;

  /**
   * Fetch audit logs with current filters.
   * If `before` is provided, appends to existing logs (pagination).
   */
  const fetchLogs = async (before = null) => {
    try {
      const params = { limit: PAGE_SIZE };

      if (eventFilter) params.event = eventFilter;
      if (before) params.before = before;

      const response = await adminApi.getAuditLog(params);
      const newLogs = response.data.logs || [];

      if (before) {
        // Append for "load more"
        setLogs((prev) => [...prev, ...newLogs]);
      } else {
        setLogs(newLogs);
      }

      // If we got fewer results than requested, there are no more pages
      setHasMore(newLogs.length === PAGE_SIZE);
    } catch (err) {
      setError(err.message);
    }
  };

  // Initial load and reload when filters change
  useEffect(() => {
    setLoading(true);
    setError('');
    fetchLogs().finally(() => setLoading(false));
  }, [eventFilter]);

  /**
   * Load next page using the timestamp of the last entry as cursor
   */
  const handleLoadMore = async () => {
    if (logs.length === 0) return;
    setLoadingMore(true);
    const lastTimestamp = logs[logs.length - 1].timestamp;
    await fetchLogs(lastTimestamp);
    setLoadingMore(false);
  };

  /**
   * Apply client-side date range filter.
   * The API doesn't have date range params, so we filter after fetch.
   * For large datasets this could be moved server-side.
   */
  const filteredLogs = logs.filter((log) => {
    if (startDate && new Date(log.timestamp) < new Date(startDate)) return false;
    if (endDate) {
      // Include the entire end date by comparing against end of day
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      if (new Date(log.timestamp) > endOfDay) return false;
    }
    return true;
  });

  // Gate: only super admins can see this page
  if (user?.role !== 'super_admin') {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">You do not have access to this page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
        <p className="mt-1 text-sm text-gray-500">Track login events and system activity</p>
      </div>

      {/* Filter Bar */}
      <div className="mb-4 flex flex-wrap items-end gap-4 bg-white p-4 rounded-lg border border-gray-200">
        {/* Event type filter */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Event Type</label>
          <select
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            className="block w-48 rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
          >
            {EVENT_TYPES.map((et) => (
              <option key={et.value} value={et.value}>{et.label}</option>
            ))}
          </select>
        </div>

        {/* Date range filters */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="block rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="block rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        {/* Clear filters */}
        {(eventFilter || startDate || endDate) && (
          <button
            onClick={() => { setEventFilter(''); setStartDate(''); setEndDate(''); }}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Clear filters
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="mt-2 text-gray-500">No audit log entries found.</p>
        </div>
      ) : (
        <>
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLogs.map((log) => (
                  <tr key={log._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateFull(log.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${eventBadgeStyles[log.event] || 'bg-gray-100 text-gray-800'}`}>
                        {eventLabels[log.event] || log.event}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.user?.email || log.metadata?.email || '--'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.company?.name || '--'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {log.metadata?.ipAddress || '--'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Load More button for cursor-based pagination */}
          {hasMore && (
            <div className="mt-4 text-center">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loadingMore ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                    Loading...
                  </>
                ) : (
                  'Load More'
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AuditLog;
